// @givmo/app-charity — client telemetry (Sentry + GA4) singletons.
//
// Day-one telemetry, mirroring the admin app's init but reading from the charity SPA env
// (src/lib/env.ts). Both packages are SSR-safe no-ops until a supported browser attaches; here they
// run on the client only. PII-safety is enforced by the packages themselves (the analytics PII
// deny-list scrub + the Sentry beforeSend scrubber) — this module never passes PII in.

import { createAnalytics, type GivmoAnalytics } from '@givmo/analytics';
import { initObservability } from '@givmo/observability';
import type { CharityEnv } from './env';
import { CHARITY_CLIENT_PLATFORM } from './constants';

let analytics: GivmoAnalytics | null = null;
let observabilityStarted = false;

/** Initialize Sentry once (no-op without a DSN). Forced PII scrubbing is owned by the package. */
export function initClientObservability(env: CharityEnv): void {
  if (observabilityStarted) return;
  observabilityStarted = true;
  initObservability({
    dsn: env.sentryDsn,
    environment: env.deployEnv,
    clientIdentity: { platform: CHARITY_CLIENT_PLATFORM, version: env.appVersion },
  });
}

/** Lazily create the GA4 analytics handle from the env Firebase config. */
export function getAnalytics(env: CharityEnv): GivmoAnalytics | null {
  if (analytics !== null) return analytics;
  analytics = createAnalytics({
    firebaseConfig: {
      apiKey: env.firebase.apiKey,
      authDomain: env.firebase.authDomain,
      projectId: env.firebase.projectId,
      appId: env.firebase.appId,
      storageBucket: env.firebase.storageBucket,
      messagingSenderId: env.firebase.messagingSenderId,
      measurementId: env.firebase.measurementId,
    },
  });
  return analytics;
}

/** Reset telemetry singletons — test-only seam. */
export function resetTelemetryForTests(): void {
  analytics = null;
  observabilityStarted = false;
}
